/**
 * Sportsbook Selector Component
 * Dropdown for selecting the user's preferred sportsbook for pick generation.
 *
 * Coverage-limited: for US leagues it shows only books with live odds coverage for the
 * league (from /api/sportsbooks/coverage), so you cannot pick a book with no data. While
 * coverage is loading it falls back to the full list (books without Odds API support are
 * disabled). EU leagues use their own book list unchanged.
 */

import {
  EU_SPORTSBOOKS,
  getSportsbookOptionsForLeague,
  limitSportsbooksToCovered,
  SPORTSBOOKS,
} from '@config/constants';
import { useClickOutside } from '@hooks/useClickOutside';
import { useSportsbookCoverage } from '@hooks/useSportsbookCoverage';
import { getOddsApiRegions, type LeagueKey } from '@parlay-picker/shared/league';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useCallback, useMemo, useRef, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface SportsbookSelectorProps {
  selected: string;
  onChange: (sportsbook: string) => void;
  openUpward?: boolean;
  /** Unset: the US list. */
  leagueKey?: LeagueKey;
}

// =============================================================================
// Component
// =============================================================================

export function SportsbookSelector({
  selected,
  onChange,
  openUpward = false,
  leagueKey,
}: SportsbookSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, close, isOpen);

  const isUs = !leagueKey || getOddsApiRegions(leagueKey) === 'us';
  const sportsbooksList = useMemo(
    () => (leagueKey ? getSportsbookOptionsForLeague(leagueKey) : Object.values(SPORTSBOOKS)),
    [leagueKey],
  );

  // Coverage-limit the US list to books that actually have data for this league.
  const covered = useSportsbookCoverage(isUs ? leagueKey : undefined);
  const coverageKnown = isUs && covered != null && covered.length > 0;
  const displayBooks = useMemo(
    () => (isUs ? limitSportsbooksToCovered(sportsbooksList, covered, selected) : sportsbooksList),
    [isUs, sportsbooksList, covered, selected],
  );
  const selectedBook =
    sportsbooksList.find((book) => book.id === selected) ??
    (isUs ? SPORTSBOOKS.fanduel : EU_SPORTSBOOKS.pinnacle);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        data-testid="sportsbook-selector-trigger"
        aria-label="Change sportsbook"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-theme-surface hover:bg-theme-surface-hover rounded-lg transition-colors text-sm"
      >
        {/* Colored dot for selected sportsbook */}
        <span className={`w-2 h-2 rounded-full ${selectedBook.dot}`} />
        <span className="text-theme-text-secondary">{selectedBook.abbr}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 w-40 bg-theme-surface rounded-lg shadow-lg py-1 z-20 border border-theme-border max-h-64 overflow-y-auto ${openUpward ? 'bottom-full mb-2' : 'mt-2'}`}
        >
          {displayBooks.map((book) => {
            // When coverage is known, every US book shown is covered (selectable). Until it
            // loads, fall back to the static Odds-API-support flag.
            const isDisabled = coverageKnown ? false : !book.oddsApiSupported;
            const isSelected = selected === book.id;

            return (
              <button
                type="button"
                key={book.id}
                onClick={() => {
                  if (!isDisabled) onChange(book.id);
                  setIsOpen(false);
                }}
                disabled={isDisabled}
                title={
                  isDisabled
                    ? 'Account linking only - not available for pick generation'
                    : undefined
                }
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  isDisabled
                    ? 'text-theme-text-tertiary cursor-not-allowed opacity-50'
                    : isSelected
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                      : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${isDisabled ? 'bg-theme-border' : book.dot}`}
                  />
                  <span>{book.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
